import { forwardRef, type ReactElement, useRef } from 'react';
import { type AriaRadioProps, useRadio } from 'react-aria';
import { type RadioGroupState } from 'react-stately';
import cn from 'classnames';
import { HStack, Text, VStack } from '@direct-frontend/components';
import { makeModifierClassname } from '../../lib/makeModifierClassname';
import styles from './Slot.module.css';

interface SlotProps {
    title: string;
    subtitle?: string;
    icon?: ReactElement;
    border?: boolean;
    addonRight?: ReactElement;
    className?: string;
    disabled?: boolean;
}

const modifier = makeModifierClassname(styles, 'slot');

export const SlotInner = forwardRef<HTMLDivElement, SlotProps>(function Slot(
    { title, subtitle, icon, border = true, addonRight, className, disabled },
    forwardedRef
) {
    return (
        <HStack
            gap="20"
            ref={forwardedRef}
            className={cn(modifier.className, className, modifier('disabled', disabled))}
        >
            {icon && (
                <HStack alignItems="center" justifyContent="center" className={styles.icon}>
                    {icon}
                </HStack>
            )}
            <HStack
                gap="20"
                className={cn(styles.body, {
                    [styles.body__border]: border,
                })}
            >
                <VStack className={styles.content} justifyContent="center">
                    <Text role="main" size="m" className={styles.title}>
                        {title}
                    </Text>
                    {subtitle && (
                        <Text role="supplementary" size="mc" className={styles.subtitle}>
                            {subtitle}
                        </Text>
                    )}
                </VStack>

                {addonRight && <HStack alignItems="center">{addonRight}</HStack>}
            </HStack>
        </HStack>
    );
});

interface SlotCheckboxProps
    extends Pick<SlotProps, 'title' | 'subtitle' | 'icon' | 'border' | 'className'>,
        AriaRadioProps {
    checkboxState: RadioGroupState;
}

export const SlotCheckbox = forwardRef<HTMLDivElement, SlotCheckboxProps>(function Slot(
    { checkboxState, title, ...props },
    forwardedRef
) {
    const ref = useRef(null);
    const { inputProps, labelProps } = useRadio(props, checkboxState, ref);

    return (
        <VStack ref={forwardedRef}>
            <label {...labelProps} aria-label={title}>
                <input {...inputProps} ref={ref} aria-label={title} className={styles.input} />
                <SlotInner
                    title={title}
                    {...props}
                    addonRight={
                        <div
                            className={cn(styles.checkbox, {
                                [styles.checkbox__checked]: inputProps.checked,
                            })}
                        />
                    }
                />
            </label>
        </VStack>
    );
});
